// src/components/notifications/HeaderNotification.jsx - Compact notification embedded in header
import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * HeaderNotification - Compact notification that slides within the universal header
 *
 * Features:
 * - Slides left to right within header breadcrumb area
 * - Compact design (fits in 56px header height)
 * - Type-based styling matching design system
 * - Auto-dismiss with progress indicator
 * - Manual dismiss button
 *
 * Animation flow:
 * 1. Slide in from left (translateX(-100%) → 0)
 * 2. Show for duration
 * 3. Slide out to right (translateX(0) → 100%)
 */
const HeaderNotification = ({ notification, onDismiss }) => {
  const [animationState, setAnimationState] = useState('entering'); // 'entering' | 'visible' | 'exiting'
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);

  const { id, type, title, message, duration = 4000 } = notification;

  // Handle dismiss with exit animation
  const handleDismiss = () => {
    if (isExiting) return; // Prevent multiple dismiss calls
    setIsExiting(true);
    setAnimationState('exiting');
    setTimeout(() => {
      onDismiss(id);
    }, 300);
  };

  // Animation lifecycle
  useEffect(() => {
    // Slide in
    const enterTimer = setTimeout(() => {
      setAnimationState('visible');
    }, 50);

    let progressInterval;
    let dismissTimer;

    // Only run countdown and auto-dismiss if duration is set (not persistent)
    if (duration) {
      const startTime = Date.now();
      progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);

        if (remaining === 0) {
          clearInterval(progressInterval);
        }
      }, 16); // ~60fps

      // Auto-dismiss - slides out to the right automatically
      dismissTimer = setTimeout(() => {
        console.log('🔔 Auto-dismissing notification:', id);
        handleDismiss();
      }, duration);
    } else {
      // Persistent notification — no progress bar
      setProgress(0);
    }

    return () => {
      clearTimeout(enterTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
      if (progressInterval) clearInterval(progressInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, duration]);

  // Icon mapping
  const icons = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertCircle,
    info: Info
  };

  const Icon = icons[type] || Info;

  // Type-based styling (compact header version)
  const typeStyles = {
    success: {
      container: 'bg-emerald-900/30 border-emerald-500/40',
      icon: 'text-emerald-400',
      text: 'text-emerald-200',
      progress: 'bg-emerald-500'
    },
    error: {
      container: 'bg-red-900/30 border-red-500/40',
      icon: 'text-red-400',
      text: 'text-red-200',
      progress: 'bg-red-500'
    },
    warning: {
      container: 'bg-amber-900/30 border-amber-500/40',
      icon: 'text-amber-400',
      text: 'text-amber-200',
      progress: 'bg-amber-500'
    },
    info: {
      container: 'bg-blue-900/30 border-blue-500/40',
      icon: 'text-blue-400',
      text: 'text-blue-200',
      progress: 'bg-blue-500'
    }
  };

  const styles = typeStyles[type] || typeStyles.info;

  // Animation classes based on state
  const getAnimationClass = () => {
    switch (animationState) {
      case 'entering':
        return '-translate-x-full opacity-0';
      case 'visible':
        return 'translate-x-0 opacity-100';
      case 'exiting':
        return 'translate-x-full opacity-0';
      default:
        return '';
    }
  };

  return (
    <div
      className={cn(
        // Base styles - compact for header
        'relative h-10 rounded-lg border backdrop-blur-sm',
        'transition-all duration-300 ease-in-out',
        'shadow-md overflow-hidden',
        // Type-specific styles
        styles.container,
        // Animation
        getAnimationClass(),
        // Fixed width to prevent layout shift
        'w-80 max-w-[320px]'
      )}
    >
      {/* Progress bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
        <div
          className={cn('h-full transition-all duration-75 ease-linear', styles.progress)}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Content */}
      <div className="relative flex items-center gap-2 px-3 h-full">
        {/* Icon */}
        <div className={cn('flex-shrink-0', styles.icon)}>
          <Icon className="w-4 h-4" />
        </div>

        {/* Text content - compact */}
        <div className="flex-1 min-w-0">
          {title && (
            <div className={cn('font-semibold text-xs leading-tight truncate', styles.text)}>
              {title}
            </div>
          )}
          {message && (
            <div className={cn('text-xs leading-tight truncate', styles.text, title ? 'opacity-90' : '')}>
              {message}
            </div>
          )}
          {!title && !message && (
            <div className={cn('text-xs leading-tight', styles.text)}>
              Notification
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className={cn(
            'flex-shrink-0 p-1 rounded transition-colors duration-200',
            'hover:bg-white/10 focus:outline-none',
            styles.text
          )}
          aria-label="Dismiss notification"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default HeaderNotification;
